import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { chatService } from "../../services/chatService";
import { FilePreviewCard } from "core/components/global/input/inputBars/FilePreviewCard";
import { createChatSocket, ChatSocket } from "../../services/chatSocketService";
import { v4 as uuidv4 } from "uuid";
import { getWorkbookfromJson } from "core/hooks/jsonToCsv";

const MODEL_ID =
  (process.env.REACT_APP_CSTIASP_MODEL as string) || "Vulnerability_Remediation";

type UploadedFileCookie = {
  fileid: string;
  filename: string;
  conversationid: string;
  createdAt: string;
};

const UPLOADED_FILE_COOKIE = "cstIasp_uploaded_file";

const setCookie = (name: string, value: string, days?: number) => {
  if (typeof days === "number") {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=/`;
    return;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/`;
};

const deleteCookie = (name: string) => {
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

const getCookie = (name: string): string | null => {
  const encodedName = encodeURIComponent(name) + "=";
  const parts = document.cookie.split(";");
  for (const rawPart of parts) {
    const part = rawPart.trim();
    if (part.startsWith(encodedName)) {
      return decodeURIComponent(part.slice(encodedName.length));
    }
  }
  return null;
};


type ChatPageProps = {
  conversationId: string;
  onConversationIdChange: (conversationId: string) => void;
};

const ChatPage: React.FC<ChatPageProps> = ({
  conversationId,
  onConversationIdChange,
}) => {
  type UiChatMessage = {
    role: "user" | "assistant" | "system";
    content: string;
    chatid?: string;
  };

  const [messages, setMessages] = useState<UiChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadCaption, setUploadCaption] = useState<string>("");
  const [socket, setSocket] = useState<ChatSocket | null>(null);
  const [signedUrlByFileKey, setSignedUrlByFileKey] = useState<Record<string, string>>({});
  const streamIdleTimeoutRef = useRef<number | null>(null);
  const titledConversationIdsRef = useRef<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const extractExcelBlock = (content: string): {
    before: string;
    excelBlock: string;
    after: string;
  } | null => {
    if (typeof content !== "string") return null;
    const openTag = "<excel>";
    const closeTag = "</excel>";
    const start = content.indexOf(openTag);
    if (start < 0) return null;

    const end = content.indexOf(closeTag, start + openTag.length);
    if (end >= 0) {
      const excelBlock = content.slice(start, end + closeTag.length);
      return {
        before: content.slice(0, start),
        excelBlock,
        after: content.slice(end + closeTag.length),
      };
    }

    // Graceful fallback if backend didn't include a closing tag.
    const raw = content.slice(start + openTag.length);
    const excelBlock = `${openTag}${raw}${closeTag}`;
    return {
      before: content.slice(0, start),
      excelBlock,
      after: "",
    };
  };

  const extractFileKeyFromPath = (path?: string): { key: string; fileName: string } | null => {
    if (!path) return null;
    // backend path: uploads/user/<userId>/hash/<hash>/file/<fileName>
    const match = path.match(/\/hash\/([^/]+)\/file\/(.+)$/);
    if (!match) return null;
    const hash = match[1];
    const fileName = match[2];
    return { key: `${hash}|${fileName}`, fileName };
  };

  const parseFileUploadMessage = (
    content: string
  ): { hash: string; fileName: string; key: string } | null => {
    if (typeof content !== "string") return null;
    if (!content.startsWith("File upload ")) return null;

    // message format saved by backend (chatRoutes.js):
    // "File upload <hash> <json fileinfo>"
    const rest = content.slice("File upload ".length);
    const firstSpace = rest.indexOf(" ");
    if (firstSpace <= 0) return null;
    const hash = rest.slice(0, firstSpace).trim();
    const jsonPart = rest.slice(firstSpace + 1).trim();

    let fileName = "";
    try {
      const info = JSON.parse(jsonPart);
      if (info && typeof info.name === "string") fileName = info.name;
    } catch {
      // tolerate non-JSON content
    }
    if (!hash || !fileName) return null;
    return { hash, fileName, key: `${hash}|${fileName}` };
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const s = createChatSocket();
    setSocket(s);

    const onStreamMessage = (raw: unknown) => {
      const text = typeof raw === "string" ? raw : JSON.stringify(raw);
      let payload: any;
      try {
        payload = JSON.parse(text);
      } catch {
        payload = { content: text };
      }

      const chatid: string | undefined = payload?.chatid ?? payload?.id;
      const chunk: string = payload?.content ?? "";
      if (!chunk) return;

      setMessages((prev) => {
        if (!chatid) {
          return [...prev, { role: "assistant", content: chunk }];
        }

        const lastIdx = [...prev]
          .map((m, idx) => ({ m, idx }))
          .reverse()
          .find(({ m }) => m.role === "assistant" && m.chatid === chatid)?.idx;

        if (lastIdx === undefined) {
          return [...prev, { role: "assistant", content: chunk, chatid }];
        }

        const next = [...prev];
        next[lastIdx] = {
          ...next[lastIdx],
          content: (next[lastIdx].content ?? "") + chunk,
        };
        return next;
      });

      // Consider streaming done when no chunks arrive for a bit.
      if (streamIdleTimeoutRef.current) {
        window.clearTimeout(streamIdleTimeoutRef.current);
      }
      streamIdleTimeoutRef.current = window.setTimeout(() => {
        setIsLoading(false);
      }, 800);
    };

    s.on("message", onStreamMessage);
    s.on("file_chunk", onStreamMessage);

    return () => {
      if (streamIdleTimeoutRef.current) {
        window.clearTimeout(streamIdleTimeoutRef.current);
      }
      s.off("message", onStreamMessage);
      s.off("file_chunk", onStreamMessage);
      s.disconnect();
      setSocket(null);
    };
  }, []);
  useEffect(() => {
    const normalizedConversationId = (conversationId || "").trim();
    setSelectedFile(null);
    setUploadCaption("");

    if (!normalizedConversationId) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const normalizeContent = (content: any): string => {
      if (typeof content === "string") return content;
      if (content && typeof content === "object") {
        if (typeof content.name === "string") return content.name;
        try {
          return JSON.stringify(content);
        } catch {
          return String(content);
        }
      }
      return content == null ? "" : String(content);
    };

    chatService
      .getConversationViaApiConversations({ conversationid: normalizedConversationId })
      .then((conversation) => {
        if (cancelled) return;

        if (!conversation) {
          setMessages([]);
          return;
        }

        titledConversationIdsRef.current.add(normalizedConversationId);

        const history = Array.isArray(conversation.messages)
          ? conversation.messages
          : [];
        const nextMessages = history.map((m: any) => ({
          role: (m?.role as "user" | "assistant" | "system") || "assistant",
          content: normalizeContent(m?.content),
          chatid: typeof m?.chatid === "string" ? m.chatid : undefined,
        }));

        setMessages(nextMessages);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load conversation history:", error);
        setMessages([
          {
            role: "assistant",
            content: "Failed to load conversation history. Please try again.",
          },
        ]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    // In parallel, load signed URLs for any uploaded files in this conversation.
    chatService
      .getConversationSignedUrlsViaApiConversations({ conversationid: normalizedConversationId })
      .then((refs) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const ref of refs) {
          const extracted = extractFileKeyFromPath(ref?.path);
          const url = ref?.signedUrl;
          if (extracted?.key && typeof url === "string" && url) {
            next[extracted.key] = url;
          }
        }
        setSignedUrlByFileKey(next);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Failed to load signed URLs:", e);
        setSignedUrlByFileKey({});
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    if (!socket) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Chat socket is not connected. Please refresh and try again." },
      ]);
      return;
    }

    const conversationid = ensureConversationId();
    const isNewConversation = messages.length === 0;
    const trimmed = input.trim();

    const userID = uuidv4();
    const assistantID = uuidv4();
    const chatid = `${userID}:${assistantID}`;

    const userMessage: UiChatMessage = {
      role: "user",
      content: trimmed,
      chatid,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    const assistantMessage: UiChatMessage = {
      role: "assistant",
      content: "",
      chatid,
    };
    setMessages((prev) => [...prev, assistantMessage]);

    // Best-effort: set conversation title once.
    if (isNewConversation && !titledConversationIdsRef.current.has(conversationid)) {
      const title = trimmed.length > 30 ? trimmed.substring(0, 30) : trimmed;
      titledConversationIdsRef.current.add(conversationid);
      chatService
        .updateConversationTitleViaApiConversations({
          conversationid,
          conversationTitle: title,
        })
        .catch(() => {
          // Non-fatal; chat still works over sockets.
          titledConversationIdsRef.current.delete(conversationid);
        });
    }

    try {
      const reqData = {
        content: trimmed,
        model: MODEL_ID,
        conversationid,
        id: chatid,
      };
      socket.emit("message", JSON.stringify(reqData));
    } catch (error) {
      console.error("Socket send error:", error);
      setIsLoading(false);
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          role: "assistant",
          content: "Sorry, an error occurred. Please try again.",
          chatid,
        },
      ]);
    }
  };

  const ensureConversationId = (): string => {
    if (conversationId && conversationId.trim() !== "") return conversationId;
    const generated = uuidv4();
    onConversationIdChange(generated);
    localStorage.setItem("cstIasp_conversationid", generated);
    return generated;
  };

  const calculateHash = async (buffer: ArrayBuffer): Promise<string> => {
    const digest = await crypto.subtle.digest("SHA-512", buffer);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  const handleUpload = async () => {
    if (!selectedFile || isLoading || isUploading) return;
    if (!socket) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Chat socket is not connected. Please refresh and try again." },
      ]);
      return;
    }

    const file = selectedFile;
    setIsUploading(true);
    setIsLoading(true);

    try {
      const ensuredConversationId = ensureConversationId();

      const isNewConversation = messages.length === 0;
      if (isNewConversation && !titledConversationIdsRef.current.has(ensuredConversationId)) {
        const title = file.name.length > 30 ? file.name.substring(0, 30) : file.name;
        titledConversationIdsRef.current.add(ensuredConversationId);
        chatService
          .updateConversationTitleViaApiConversations({
            conversationid: ensuredConversationId,
            conversationTitle: title,
          })
          .catch(() => {
            titledConversationIdsRef.current.delete(ensuredConversationId);
          });
      }

      // Mirror SecAI chatid pattern.
      const userID = uuidv4();
      const assistantID = uuidv4();
      const chatid = `${userID}:${assistantID}`;

      const arrayBuffer = await file.arrayBuffer();
      const fileHash = await calculateHash(arrayBuffer);

      const fileinfoForMessage = {
        name: file.name,
        hash: fileHash,
        type: file.type,
        size: file.size,
      };
      const fileUploadMarker = `File upload ${fileHash} ${JSON.stringify(fileinfoForMessage)}`;

      // Show the user action + create an assistant placeholder for streaming.
      setMessages((prev) => [
        ...prev,
        { role: "user", content: fileUploadMarker, chatid },
        { role: "assistant", content: "", chatid },
      ]);

      // Clear any REST-era uploaded-file cookie; socket uploads don't return a fileid.
      deleteCookie(UPLOADED_FILE_COOKIE);
      const chunkSize = 1024 * 1024; // 1MB
      const totalChunks = Math.ceil(file.size / chunkSize);
      let position = 0;
      const reader = new FileReader();

      reader.onerror = function (errorEvent) {
        if (errorEvent.target !== null) {
          console.error("Error reading file:", (errorEvent.target as any).error);
          reader.abort();
        }
      };

      reader.onload = async function (loadEvent: any) {
        if (!loadEvent.target) return;
        try {
          const fileData = {
            model: MODEL_ID,
            conversationid: ensuredConversationId,
            id: chatid,
            fileinfo: {
              name: file.name,
              hash: fileHash,
              type: file.type,
              size: file.size,
              position,
              totalchunks: totalChunks,
            },
            content: new Uint8Array(loadEvent.target.result),
          };

          socket.emit("file_chunk", fileData);

          position += chunkSize;
          if (position < file.size) {
            readNextChunk();
          } else {
            setUploadCaption("File sent");
            setSelectedFile(null);
            setInput("");
            setIsUploading(false);

            // Refresh signed URLs so the file becomes downloadable in history.
            const targetKey = `${fileHash}|${file.name}`;
            let attempts = 0;
            const refresh = async () => {
              attempts += 1;
              try {
                const refs = await chatService.getConversationSignedUrlsViaApiConversations({
                  conversationid: ensuredConversationId,
                });
                const next: Record<string, string> = {};
                for (const ref of refs) {
                  const extracted = extractFileKeyFromPath(ref?.path);
                  const url = ref?.signedUrl;
                  if (extracted?.key && typeof url === "string" && url) {
                    next[extracted.key] = url;
                  }
                }
                setSignedUrlByFileKey(next);

                if (!next[targetKey] && attempts < 3) {
                  window.setTimeout(refresh, 1500);
                }
              } catch {
                if (attempts < 3) {
                  window.setTimeout(refresh, 1500);
                }
              }
            };

            refresh();
          }
        } catch (error) {
          console.error("Error during file upload:", error);
          setIsUploading(false);
          setIsLoading(false);
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "File upload failed. Please try again.",
              chatid,
            },
          ]);
        }
      };

      const readNextChunk = () => {
        if (position >= file.size) return;
        const nextChunkEnd = Math.min(position + chunkSize, file.size);
        const chunk = file.slice(position, nextChunkEnd);
        reader.readAsArrayBuffer(chunk);
      };

      readNextChunk();
    } catch (err) {
      console.error("Upload failed", err);
      setUploadCaption("");
      setIsUploading(false);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "File upload failed. Please try again.",
        },
      ]);
      setIsLoading(false);
    } finally {
      // For socket uploads, isUploading flips off when chunks finish.
    }
  };

  const handleDownload = async () => {
    // No-op: socket uploads don't provide a fileid for REST downloads.
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Match SecAI Chat UX: when a file is selected, Enter should not trigger upload.
      if (selectedFile) return;
      handleSend();
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Card sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <CardContent
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
          }}
        >
          {uploadCaption && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Typography variant="caption" color="text.secondary">
                {uploadCaption}
              </Typography>
            </Box>
          )}

          {messages.length === 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <Typography>Start a conversation with the AI assistant</Typography>
            </Box>
          )}

          {messages.map((message, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                maxWidth: "80%",
                alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                bgcolor: message.role === "user" ? "primary.main" : "grey.100",
                color: message.role === "user" ? "white" : "text.primary",
              }}
            >
              {(() => {
                const fileUpload = parseFileUploadMessage(message.content);
                if (message.role === "user" && fileUpload) {
                  const url = signedUrlByFileKey[fileUpload.key];
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ wordBreak: "break-word" }}>
                        {fileUpload.fileName}
                      </Typography>
                      <IconButton
                        size="small"
                        disabled={!url}
                        title={url ? "Download" : "Preparing download link"}
                        onClick={() => {
                          if (!url) return;
                          window.open(url, "_blank", "noopener,noreferrer");
                        }}
                        sx={{ color: "inherit" }}
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                }

                if (message.role === "assistant") {
                  const excelParts = extractExcelBlock(message.content || "");
                  if (excelParts) {
                    const before = excelParts.before?.trim();
                    const after = excelParts.after?.trim();
                    return (
                      <Box>
                        {before ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {before}
                          </ReactMarkdown>
                        ) : null}

                        <Box sx={{ mt: before ? 1 : 0, mb: after ? 1 : 0 }}>
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={async (e) => {
                              e.preventDefault();
                              const ok = await getWorkbookfromJson(excelParts.excelBlock);
                              if (!ok) {
                                console.error("Failed to parse <excel> content");
                              }
                            }}
                            onKeyDown={async (e) => {
                              if (e.key !== "Enter" && e.key !== " ") return;
                              e.preventDefault();
                              const ok = await getWorkbookfromJson(excelParts.excelBlock);
                              if (!ok) {
                                console.error("Failed to parse <excel> content");
                              }
                            }}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              cursor: "pointer",
                              px: 1,
                              py: 0.75,
                              borderRadius: 1,
                              bgcolor: "background.paper",
                              border: 1,
                              borderColor: "divider",
                            }}
                          >
                            <FileDownloadIcon fontSize="small" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Download Excel Report
                            </Typography>
                          </Box>
                        </Box>

                        {after ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {after}
                          </ReactMarkdown>
                        ) : null}
                      </Box>
                    );
                  }

                  return (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content || "..."}
                    </ReactMarkdown>
                  );
                }

                return <Typography>{message.content}</Typography>;
              })()}
            </Paper>
          ))}

          {isLoading && messages[messages.length - 1]?.content === "" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Thinking...
              </Typography>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </CardContent>

        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 1,
          }}
        >
          {selectedFile && (
            <Box sx={{ alignSelf: "flex-end" }}>
              <FilePreviewCard
                file={selectedFile}
                onRemove={() => {
                  setSelectedFile(null);
                  setUploadCaption("");
                }}
              />
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            sx={{ bgcolor: "background.paper" }}
          />

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const files = e.target.files;
              if (!files || files.length === 0) return;
              const file = files[0];
              e.currentTarget.value = "";

              setSelectedFile(file);
              setUploadCaption("");
            }}
          />

          <IconButton
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isLoading}
            sx={{ alignSelf: "flex-end" }}
            title="Attach file"
          >
            {isUploading ? <CircularProgress size={20} /> : <AttachFileIcon />}
          </IconButton>

          <IconButton
            color="primary"
            onClick={selectedFile ? handleUpload : handleSend}
            disabled={selectedFile ? isUploading || isLoading : !input.trim() || isLoading}
            sx={{ alignSelf: "flex-end" }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Card>
    </Box>
  );
};

export default ChatPage;
